/** Shared, environment-driven browser transport. Session cookies stay HttpOnly. */

import { nextClientRequestId, recordActivity } from '../lib/recentActivity';

export type ApiFailureKind = 'network' | 'timeout' | 'http' | 'malformed';

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly kind: ApiFailureKind;
  /** The response's `Retry-After` in whole seconds, when it sent one (e.g. `workspace_waking`). */
  readonly retryAfterSeconds: number | null;

  constructor(kind: ApiFailureKind, status: number, code: string, message: string, retryAfterSeconds: number | null = null) {
    super(message);
    this.name = 'ApiClientError';
    this.kind = kind;
    this.status = status;
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** `Retry-After` as delta-seconds (the only form the API sends); anything else is `null`. */
export function retryAfterSeconds(response: { headers?: { get(name: string): string | null } }): number | null {
  const raw = (response.headers?.get('retry-after') || '').trim();
  if (!/^[0-9]{1,6}$/.test(raw)) return null;
  return Number(raw);
}

let testConfiguration: { baseUrl?: string; timeoutMs?: number } | null = null;

/** Test-only seam used by the real-process integration suite; production uses Vite env. */
export function configureApiClientForTests(configuration: { baseUrl?: string; timeoutMs?: number } | null): void {
  testConfiguration = configuration;
}

function configuredBaseUrl(): string {
  const viteEnv = (import.meta as ImportMeta & { env?: ImportMetaEnv }).env;
  const raw = (testConfiguration?.baseUrl ?? viteEnv?.VITE_AUTEUR_API_BASE_URL ?? '').trim();
  if (!raw) return '';
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    throw new ApiClientError(
      'malformed',
      0,
      'invalid_api_configuration',
      'VITE_AUTEUR_API_BASE_URL must be an absolute HTTP(S) origin',
    );
  }
  if (!['http:', 'https:'].includes(parsed.protocol) || parsed.username || parsed.password) {
    throw new ApiClientError(
      'malformed',
      0,
      'invalid_api_configuration',
      'VITE_AUTEUR_API_BASE_URL must be an HTTP(S) origin without credentials',
    );
  }
  return raw.replace(/\/$/, '');
}

function configuredTimeoutMs(): number {
  if (testConfiguration?.timeoutMs !== undefined) return testConfiguration.timeoutMs;
  const viteEnv = (import.meta as ImportMeta & { env?: ImportMetaEnv }).env;
  const raw = (viteEnv?.VITE_AUTEUR_API_TIMEOUT_MS || '').trim();
  if (!raw) return 15_000;
  const timeout = Number(raw);
  if (!Number.isFinite(timeout) || timeout < 1 || timeout > 120_000) {
    throw new ApiClientError(
      'malformed',
      0,
      'invalid_api_configuration',
      'VITE_AUTEUR_API_TIMEOUT_MS must be between 1 and 120000',
    );
  }
  return timeout;
}

export function apiUrl(path: string): string {
  if (!path.startsWith('/')) {
    throw new ApiClientError('malformed', 0, 'invalid_api_path', 'API paths must begin with /');
  }
  return `${configuredBaseUrl()}${path}`;
}

function errorFromPayload(payload: unknown): { code: string; message: string } | null {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  const error = (payload as Record<string, unknown>).error;
  if (!error || typeof error !== 'object' || Array.isArray(error)) return null;
  const record = error as Record<string, unknown>;
  if (typeof record.code !== 'string' || typeof record.message !== 'string') return null;
  return { code: record.code, message: record.message };
}

export async function requestJson(
  path: string,
  init: RequestInit = {},
  timeoutMs = configuredTimeoutMs(),
): Promise<unknown> {
  const requestId = nextClientRequestId();
  const controller = new AbortController();
  const timer = globalThis.setTimeout(() => controller.abort(), timeoutMs);
  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      ...init,
      credentials: 'include',
      signal: controller.signal,
      headers: {
        accept: 'application/json',
        'x-client-request-id': requestId,
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        ...(init.headers || {}),
      },
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      recordActivity(requestId, 'request_timeout');
      throw new ApiClientError('timeout', 0, 'request_timeout', 'the API request timed out');
    }
    recordActivity(requestId, 'network_error');
    throw new ApiClientError('network', 0, 'network_error', 'the API could not be reached');
  } finally {
    globalThis.clearTimeout(timer);
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    if (!response.ok) {
      throw new ApiClientError(
        'http',
        response.status,
        'request_failed',
        `the API returned HTTP ${response.status}`,
      );
    }
    throw new ApiClientError(
      'malformed',
      response.status,
      'malformed_response',
      'the API returned a malformed JSON response',
    );
  }

  if (!response.ok) {
    const error = errorFromPayload(payload);
    recordActivity(requestId, error?.code || 'request_failed');
    throw new ApiClientError(
      'http',
      response.status,
      error?.code || 'request_failed',
      error?.message || `the API returned HTTP ${response.status}`,
      retryAfterSeconds(response),
    );
  }
  recordActivity(requestId);
  if (!payload || typeof payload !== 'object') {
    throw new ApiClientError(
      'malformed',
      response.status,
      'malformed_response',
      'the API response must be a JSON object or array',
    );
  }
  return payload;
}

export function responseRecord(payload: unknown, field?: string): Record<string, unknown> {
  const value = field ? (payload as Record<string, unknown>)[field] : payload;
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new ApiClientError(
      'malformed',
      200,
      'malformed_response',
      field ? `the API response is missing the ${field} object` : 'the API response is malformed',
    );
  }
  return value as Record<string, unknown>;
}

export function responseArray(payload: unknown, field?: string): unknown[] {
  const value = field ? (payload as Record<string, unknown>)[field] : payload;
  if (!Array.isArray(value)) {
    throw new ApiClientError(
      'malformed',
      200,
      'malformed_response',
      field ? `the API response is missing the ${field} array` : 'the API response is malformed',
    );
  }
  return value;
}
